import { extractFunctionName, tokenizeVariableContent } from './template-variable-tokenizer.util'

const VALID_NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/

const TYPE_VALIDATOR_SET = new Set<string>(['text', 'number', 'url', 'email', 'enum'])

const NUMERIC_CONSTRAINT_SET = new Set<string>(['min', 'max', 'minLength', 'maxLength'])

const ALL_CONSTRAINT_SET = new Set<string>([...NUMERIC_CONSTRAINT_SET, 'regex'])

const MODIFIER_SET = new Set<string>(['label', 'helper', 'default'])

const ALL_KNOWN_TOKEN_SET = new Set<string>([
  ...TYPE_VALIDATOR_SET,
  ...ALL_CONSTRAINT_SET,
  ...MODIFIER_SET,
])

const hasBalancedParens = (token: string): boolean => {
  let depth = 0
  for (const ch of token) {
    if (ch === '(') depth++
    if (ch === ')') depth--
    if (depth < 0) return false
  }
  return depth === 0
}

const extractInnerParenValue = (token: string): string | null => {
  const openIdx = token.indexOf('(')
  const closeIdx = token.lastIndexOf(')')
  if (openIdx === -1 || closeIdx === -1) return null
  return token.slice(openIdx + 1, closeIdx).trim()
}

const isValidConstraintValue = (fnName: string, token: string): boolean => {
  const inner = extractInnerParenValue(token)
  if (!inner || inner.length === 0) return false

  if (NUMERIC_CONSTRAINT_SET.has(fnName)) {
    return !Number.isNaN(Number(inner))
  }

  return true
}

const isSimpleTypeToken = (fnName: string, hasParens: boolean): boolean =>
  !hasParens && TYPE_VALIDATOR_SET.has(fnName) && fnName !== 'enum'

const isValidEnumToken = (token: string): boolean => {
  const inner = extractInnerParenValue(token)
  return inner !== null && inner.length > 0
}

const isValidFunctionToken = (fnName: string, token: string, hasParens: boolean): boolean => {
  if (!hasParens) return false

  if (fnName === 'enum') return isValidEnumToken(token)

  return isValidConstraintValue(fnName, token)
}

const isTokenValid = (token: string): boolean => {
  if (!hasBalancedParens(token)) return false

  const fnName = extractFunctionName(token)
  if (!ALL_KNOWN_TOKEN_SET.has(fnName)) return false

  const hasParens = token.includes('(')
  if (isSimpleTypeToken(fnName, hasParens)) return true

  return isValidFunctionToken(fnName, token, hasParens)
}

/**
 * Validates the inner content of a template variable block (without the `{` and `}`).
 * Returns true if the variable syntax is correct.
 */
export const isValidVariableContent = (inner: string): boolean => {
  const tokenList = tokenizeVariableContent(inner)
  if (tokenList.length === 0) return false

  const name = tokenList[0]
  if (!name || !VALID_NAME_PATTERN.test(name)) return false

  for (let i = 1; i < tokenList.length; i++) {
    if (!isTokenValid(tokenList[i])) return false
  }

  return true
}
